/* @flow */

import { normalizeEvent } from '@braindate/domain/lib/event/util';
import { DateTime } from 'luxon';

import * as types from './eventActionTypes';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';
import { topicOneOnOneKind } from '@braindate/domain/lib/topic/setting';
import {
  forStartupParam,
  eventEndpoint,
  timeSlotsSuggestionsEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';

import type { TimeSlot } from '@braindate/domain/lib/base/type';
import type { GetState } from 'src/shared/core/type/reduxTypes';
import type { TopicKind } from '@braindate/domain/lib/topic/type';

// Single event

export function fetchEvent(id: number | string, data?: Object) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchEventRequest());

    return get(eventEndpoint(id), data)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeEvent(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(fetchEventSuccess(result));

        return json;
      })
      .catch((exception) => {
        dispatch(fetchEventFailure(exception));

        throw exception;
      });
  };
}

export function fetchEventRequest() {
  return {
    type: types.FETCH_EVENT_REQUEST,
  };
}

export function fetchEventSuccess(result: number) {
  return {
    type: types.FETCH_EVENT_SUCCESS,
    result,
  };
}

export function fetchEventFailure(exception: Error) {
  return {
    type: types.FETCH_EVENT_FAILURE,
    exception,
  };
}

// Reset

export function resetEvent() {
  return {
    type: types.RESET_EVENT,
  };
}

// Offer braindate time slots suggestions

export function fetchTimeSlotsSuggestions(
  eventId: number,
  topicKind: TopicKind,
  date: DateTime,
  params?: Object,
) {
  if (topicKind === topicOneOnOneKind && (!params || !params.other_user)) {
    throw new Error(
      'other_user should be defined in params when topicKind is "offer"',
    );
  }
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchTimeSlotsSuggestionsRequest(topicKind));

    const dateInterval = {
      start_time: date.toLocal().startOf('month').toISO(),
      end_time: date.toLocal().endOf('month').toISO(),
    };

    return get(timeSlotsSuggestionsEndpoint(eventId), {
      ...dateInterval,
      kind: topicKind,
      ...params,
    })
      .then(parseResponse)
      .then((json) => {
        dispatch(
          fetchTimeSlotsSuggestionsSuccess(
            json,
            eventId,
            topicKind,
            dateInterval,
          ),
        );
      })
      .catch((e) => {
        dispatch(fetchTimeSlotsSuggestionsFailure(topicKind, e));

        throw e;
      });
  };
}

// Public

export function fetchPublicEvent(id: number | string) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchPublicEventRequest());

    return get(eventEndpoint(id), { [forStartupParam]: true })
      .then(parseResponse)
      .then((json) => {
        dispatch(fetchPublicEventSuccess(json.id, json));

        return json;
      })
      .catch((e) => {
        dispatch(fetchPublicEventFailure(e));

        throw e;
      });
  };
}

export function fetchPublicEventRequest() {
  return {
    type: types.FETCH_PUBLIC_EVENT_REQUEST,
  };
}

export function fetchPublicEventSuccess(result: number, entities: Object) {
  return {
    type: types.FETCH_PUBLIC_EVENT_SUCCESS,
    result,
    entities,
  };
}

export function fetchPublicEventFailure(exception: Error) {
  return {
    type: types.FETCH_PUBLIC_EVENT_FAILURE,
    exception,
  };
}

// Offer braindate time slots suggestions

export function fetchTimeSlotsSuggestionsRequest(topicKind: TopicKind) {
  return {
    type: types.FETCH_TIME_SLOTS_SUGGESTIONS_REQUEST,
    topicKind,
  };
}

export function fetchTimeSlotsSuggestionsSuccess(
  result: Array<TimeSlot>,
  eventId: number,
  topicKind: TopicKind,
  dateInterval: TimeSlot,
) {
  return {
    type: types.FETCH_TIME_SLOTS_SUGGESTIONS_SUCCESS,
    result,
    eventId,
    topicKind,
    dateInterval,
  };
}

export function fetchTimeSlotsSuggestionsFailure(
  topicKind: TopicKind,
  exception: Error,
) {
  return {
    type: types.FETCH_TIME_SLOTS_SUGGESTIONS_FAILURE,
    topicKind,
    exception,
  };
}
